import React, { useState } from 'react';
import './ConfirmationModal.css';

interface SampleQuotes {
  channel1: string | null;
  channel2: string | null;
}

interface ConfirmSpeakersModalProps {
  isOpen: boolean;
  channelTeacher: 1 | 2;
  sampleQuotes: SampleQuotes | null;
  /** Whether the speaker mapping is currently locked (any AI insights job exists). */
  locked: boolean;
  /** Whether prior AI insights data already exists (changes button label + body text). */
  hasPriorData: boolean;
  onSwap: () => void;
  onGenerate: () => void;
  onCancel: () => void;
  submitting?: boolean;
  swapError?: string | null;
  /** Transcript flagged unhealthy (desktop-audio leak) — warn, don't block. */
  unhealthy?: boolean;
  unhealthyReason?: string | null;
}

const ConfirmSpeakersModal: React.FC<ConfirmSpeakersModalProps> = ({
  isOpen,
  channelTeacher,
  sampleQuotes,
  locked,
  hasPriorData,
  onSwap,
  onGenerate,
  onCancel,
  submitting = false,
  swapError = null,
  unhealthy = false,
  unhealthyReason = null
}) => {
  const [swapping, setSwapping] = useState(false);
  if (!isOpen) return null;

  const teacherSample = channelTeacher === 1 ? sampleQuotes?.channel1 : sampleQuotes?.channel2;
  const clientSample = channelTeacher === 1 ? sampleQuotes?.channel2 : sampleQuotes?.channel1;

  const title = hasPriorData
    ? 'Re-run insights — speakers locked'
    : 'Generate insights — confirm speakers';

  const handleSwap = async () => {
    setSwapping(true);
    try {
      await onSwap();
    } finally {
      setSwapping(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="modal-body">
          {unhealthy && (
            <div style={{ marginBottom: 12, padding: '8px 10px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 'var(--radius)', color: '#92400e', fontSize: 13 }}>
              <strong>⚠ Unhealthy transcript.</strong> Client speech leaked onto the teacher
              track — insights may attribute the client's words to the teacher. You can still continue.
              {unhealthyReason && <div style={{ marginTop: 4, fontSize: 12 }}>{unhealthyReason}</div>}
            </div>
          )}
          <p style={{ marginTop: 0 }}>
            Confirm which channel is the Teacher and which is the Client. AI extraction labels
            quotes by speaker — getting this wrong inverts who said what.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', rowGap: 10, columnGap: 12, margin: '12px 0' }}>
            <div style={{ fontWeight: 600 }}>Teacher</div>
            <div style={{ color: '#444' }}>
              <small style={{ color: '#888' }}>Channel {channelTeacher}</small>
              <div style={{ marginTop: 2 }}>
                {teacherSample ? `"${teacherSample}"` : <em style={{ color: '#999' }}>(no sample available)</em>}
              </div>
            </div>
            <div style={{ fontWeight: 600 }}>Client</div>
            <div style={{ color: '#444' }}>
              <small style={{ color: '#888' }}>Channel {channelTeacher === 1 ? 2 : 1}</small>
              <div style={{ marginTop: 2 }}>
                {clientSample ? `"${clientSample}"` : <em style={{ color: '#999' }}>(no sample available)</em>}
              </div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 6 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleSwap}
              disabled={locked || swapping || submitting}
              style={{ fontSize: 13 }}
              title={locked ? 'Clear AI-generated insights to unlock the speaker mapping' : 'Flip Teacher and Client channels'}
            >
              {swapping ? 'Swapping…' : 'Swap speakers'}
            </button>
            {locked && (
              <small style={{ display: 'block', marginTop: 6, color: '#888' }}>
                The speaker mapping is locked because AI-generated insights already exist for this consultation.
                Clear them first to re-confirm.
              </small>
            )}
            {swapError && (
              <small style={{ display: 'block', marginTop: 6, color: '#c0392b' }}>{swapError}</small>
            )}
          </div>

          {hasPriorData && (
            <p style={{ fontSize: 13, color: '#555', marginTop: 12, marginBottom: 0 }}>
              Re-run will add new tasks and re-extract mistakes. Existing tasks and their
              done-state will not be touched. Goal, profession, and general insights that are
              already set will not change.
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onGenerate} disabled={submitting}>
            {submitting ? 'Starting…' : (hasPriorData ? 'Re-run' : 'Generate')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSpeakersModal;
